import { useEffect, useState } from 'react';
import { ApiError } from '@/api/client';
import { money } from '@/lib/format';
import { refreshStatement, useGet } from './api';
import type { StatementPayload } from './types';
import s from './StatementRefreshDialog.module.css';

export function StatementRefreshDialog({ saved, onClose, onDone }: { saved: StatementPayload; onClose: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const preview = useGet<StatementPayload>('/api/settlements/statements', {
    scope: saved.scope, partnerId: saved.partner.id, month: saved.period, dateBasis: saved.dateBasis || undefined,
  });
  const fresh = preview.fresh ? preview.data : undefined;
  const readOnly = import.meta.env.VITE_READONLY !== '0';

  useEffect(() => {
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [busy, onClose]);

  const submit = async () => {
    if (!saved.id || !saved.updatedAt || !fresh || busy || uncertain || readOnly) return;
    setBusy(true); setError('');
    try {
      await refreshStatement(saved.id, saved.updatedAt);
      onDone();
    } catch (cause) {
      const disconnected = cause instanceof ApiError && cause.status === 0;
      if (disconnected) setUncertain(true);
      setError(`${cause instanceof Error ? cause.message : '重新取数失败。'}${disconnected ? ' 请关闭窗口刷新对账单，确认结果后再操作。' : ''}`);
    } finally { setBusy(false); }
  };

  return <div className={s.scrim} onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
    <section className={s.dialog} role="dialog" aria-modal="true" aria-label={`重新取数 ${saved.statementNo}`}>
      <header><h2>重新取数 · {saved.statementNo}</h2><button type="button" aria-label="关闭" disabled={busy} onClick={onClose}>×</button></header>
      <div className={s.body}>
        <p className={s.warn}>将用当前业务单据和核销记录替换这份草稿的全部快照行与汇总金额。原对账单号保留；已确认或已锁账的单据不能重新取数。</p>
        {saved.pdfFileUrl && <p className={s.error}>这份草稿已有归档 PDF，为避免旧文件与新快照不一致，暂不能重新取数；请先在老版处理归档文件。</p>}
        {preview.error ? <p className={s.error}>最新数据加载失败：{preview.error.message} <button type="button" onClick={preview.reload}>重试</button></p>
          : !fresh ? <p className={s.loading}>正在读取最新单据…</p>
            : <div className={s.compare}><div><span>当前草稿</span><b>{saved.businessDocuments.length} 张单据</b><strong>{money(saved.summary.billAmount)}</strong></div><div><span>重新取数后</span><b>{fresh.businessDocuments.length} 张单据</b><strong>{money(fresh.summary.billAmount)}</strong></div></div>}
        {readOnly && <p className={s.note}>生产 V2 当前只读，无法替换草稿快照。</p>}
        {error && <p className={s.error} role="alert">{error}</p>}
      </div>
      <footer><button type="button" onClick={onClose} disabled={busy}>保留当前快照</button><button type="button" className={s.primary} disabled={!fresh || !saved.updatedAt || !!saved.pdfFileUrl || busy || uncertain || readOnly} onClick={() => void submit()}>{busy ? '更新中…' : '确认重新取数'}</button></footer>
    </section>
  </div>;
}
